
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchEmployees } from "../redux/employer-employeeSlice/EmployerEmployeeSlice";

import { toast } from "sonner";
import axios from "axios";

const BenefitOverview = () => {
  const dispatch = useDispatch();
  const { currentUser } = useSelector((state) => state.user || {});
  const { employees, status, error } = useSelector((state) => state.employerEmployee || {});
  const [employeeBenefits, setEmployeeBenefits] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch employees and their benefits
  useEffect(() => {
    if (!currentUser?._id || currentUser.userType !== "superAdmin") {
    //   toast.error("Access denied. Super admin privileges required.");
    //   return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch all employees
        await dispatch(fetchEmployees({ userType: "all" })).unwrap();

        // Fetch benefits for each employee
        const benefitsPromises = employees.map(async (employee) => {
          try {
            const response = await axios.post(
              `${process.env.REACT_APP_API_URL}benefit/getUserBenefit`,
              { applicantId: employee._id },
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
              }
            );
            return {
              ...employee,
              benefits: response.data.data || [],
            };
          } catch (err) {
            console.error(`Failed to fetch benefits for ${employee._id}:`, err);
            return { ...employee, benefits: [] };
          }
        });

        const results = await Promise.all(benefitsPromises);
        setEmployeeBenefits(results);
      } catch (err) {
        toast.error("Failed to load employee data or benefits");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser?._id, currentUser?.role, dispatch, employees]);

  // Calculate total benefits for an employee
  const calculateEmployeeBenefits = (benefits) => {
    const totals = { essential: 0, health: 0 };
    benefits
      .filter((b) => b.benefitStatus === "Approved")
      .forEach((benefit) => {
        const amount = parseFloat(benefit.amountRequested) || 0;
        totals[benefit.benefitType] += amount;
      });
    return totals;
  };

  if (loading || status === "loading") {
    return <div className="flex items-center justify-center h-screen bg-gray-100">Loading...</div>;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen text-red-500 bg-gray-100">
        {typeof error === "string" ? error : "An error occurred while loading data"}
      </div>
    );
  }

  if (currentUser?.role !== "superAdmin") {
    return (
      <div className="flex items-center justify-center h-screen text-red-500 bg-gray-100">
        Access denied. Super admin privileges required.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Employee Benefits Overview</h2>
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="overflow-x-auto">
          <table className="w-full bg-gray-50 border border-gray-200">
            <thead className="bg-orange-400 text-white">
              <tr>
                <th className="py-3 px-4 text-left">Employee Name</th>
                <th className="py-3 px-4 text-left">Email</th>
                <th className="py-3 px-4 text-left">Essential Benefits (₦)</th>
                <th className="py-3 px-4 text-left">Health Benefits (₦)</th>
                <th className="py-3 px-4 text-left">Total Benefits (₦)</th>
              </tr>
            </thead>
            <tbody>
              {employeeBenefits.length > 0 ? (
                employeeBenefits.map((employee, i) => {
                  const totals = calculateEmployeeBenefits(employee.benefits || []);
                  return (
                    <tr key={i} className="border-t hover:bg-gray-100">
                      <td className="py-3 px-4">{employee.firstName || "N/A"} {employee.lastName || ""}</td>
                      <td className="py-3 px-4">{employee.email || "N/A"}</td>
                      <td className="py-3 px-4">{totals.essential.toLocaleString()}</td>
                      <td className="py-3 px-4">{totals.health.toLocaleString()}</td>
                      <td className="py-3 px-4">{(totals.essential + totals.health).toLocaleString()}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="5" className="py-3 px-4 text-center text-gray-500">
                    No employee benefits data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BenefitOverview;
